import { db } from '@/js/firebase';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { logger } from './loggerService';
import {
  getBusinessDaysBetween,
  getCalendarDaysBetween,
  toDate
} from '@/utils/businessDays';
import {
  DGF_DEFAULT_RULES,
  DGF_CATEGORIES,
  DGF_OPERATORS,
  DGF_TIME_UNITS,
  DGF_ENTITY_TYPES
} from '@/constants/dgfDefaultRules';
import { PROJECT_STATUS } from '@/utils/projectUtils';

/**
 * DGF Report Service
 * Provides data fetching for the DGF Report page
 */
class DGFReportService {
  constructor() {
    this.logger = logger;
    this.rulesCache = null;
    this.rulesCacheTimestamp = null;
    this.RULES_CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Get enabled DGF rules with caching
   * Falls back to default rules if DGFRules collection is empty
   * @param {boolean} bypassCache - Force fresh fetch
   * @returns {Promise<Array>} Enabled rules
   */
  async getEnabledRules(bypassCache = false) {
    try {
      // Check cache
      const now = Date.now();
      if (
        !bypassCache &&
        this.rulesCache &&
        this.rulesCacheTimestamp &&
        now - this.rulesCacheTimestamp < this.RULES_CACHE_DURATION_MS
      ) {
        return this.rulesCache;
      }

      const rulesRef = collection(db, 'DGFRules');
      const q = query(rulesRef, where('enabled', '==', true), orderBy('sortOrder', 'asc'));
      const snapshot = await getDocs(q);

      let rules = [];
      snapshot.forEach((docSnap) => {
        rules.push({ id: docSnap.id, ...docSnap.data() });
      });

      // If no rules in database, use defaults
      if (rules.length === 0) {
        this.logger.info('[DGFReportService] No rules in DB, using defaults');
        rules = DGF_DEFAULT_RULES.filter(r => r.enabled !== false);
      }

      // Update cache
      this.rulesCache = rules;
      this.rulesCacheTimestamp = now;

      return rules;
    } catch (error) {
      this.logger.warn('[DGFReportService] Failed to get rules, using defaults', error);
      // Return defaults on error
      return DGF_DEFAULT_RULES.filter(r => r.enabled !== false);
    }
  }

  /**
   * Get rule configuration by category
   * @param {string} category - The category key
   * @returns {Promise<Object|null>} Rule config or null
   */
  async getRuleByCategory(category) {
    const rules = await this.getEnabledRules();
    return rules.find(r => r.category === category) || null;
  }

  /**
   * Check if a category should be included in badge count
   * @param {string} category - The category key
   * @returns {Promise<boolean>}
   */
  async shouldIncludeInBadgeCount(category) {
    const rule = await this.getRuleByCategory(category);
    // Default to true if no rule found or includeInBadgeCount is not set
    return rule ? rule.includeInBadgeCount !== false : true;
  }

  /**
   * Clear the rules cache
   */
  clearRulesCache() {
    this.rulesCache = null;
    this.rulesCacheTimestamp = null;
    this.logger.info('[DGFReportService] Rules cache cleared');
  }

  /**
   * Evaluate if an actual value matches a condition using an operator
   * @param {*} actualValue - The value from the entity
   * @param {string} operator - The operator (equals, not_equals, in, not_in, >, <, >=, <=)
   * @param {*} expectedValue - The value to compare against
   * @returns {boolean}
   */
  evaluateOperator(actualValue, operator, expectedValue) {
    switch (operator) {
      case DGF_OPERATORS.EQUALS:
        // Handle array comparison for empty arrays
        if (Array.isArray(expectedValue) && expectedValue.length === 0) {
          return !actualValue || (Array.isArray(actualValue) && actualValue.length === 0);
        }
        return actualValue === expectedValue;

      case DGF_OPERATORS.NOT_EQUALS:
        if (Array.isArray(expectedValue) && expectedValue.length === 0) {
          return actualValue && Array.isArray(actualValue) && actualValue.length > 0;
        }
        return actualValue !== expectedValue;

      case DGF_OPERATORS.IN:
        if (!Array.isArray(expectedValue)) return false;
        return expectedValue.includes(actualValue);

      case DGF_OPERATORS.NOT_IN:
        if (!Array.isArray(expectedValue)) return true;
        return !expectedValue.includes(actualValue);

      case DGF_OPERATORS.GREATER_THAN:
        return actualValue > expectedValue;

      case DGF_OPERATORS.LESS_THAN:
        return actualValue < expectedValue;

      case DGF_OPERATORS.GREATER_THAN_OR_EQUAL:
        return actualValue >= expectedValue;

      case DGF_OPERATORS.LESS_THAN_OR_EQUAL:
        return actualValue <= expectedValue;

      default:
        this.logger.warn('[DGFReportService] Unknown operator', { operator });
        return false;
    }
  }

  /**
   * Check if an entity matches additional filters defined in a rule
   * @param {Object} entity - The entity to check (deliverable, project, event)
   * @param {Array} filters - Array of filter conditions
   * @returns {boolean} - True if all filters pass
   */
  matchesAdditionalFilters(entity, filters) {
    if (!filters || filters.length === 0) return true;

    for (const filter of filters) {
      const actualValue = entity[filter.field];
      if (!this.evaluateOperator(actualValue, filter.operator, filter.value)) {
        return false;
      }
    }
    return true;
  }

  /**
   * Evaluate a time threshold against a reference date
   * @param {Date} referenceDate - The date to measure from
   * @param {Object} threshold - The threshold config { enabled, unit, operator, value }
   * @param {Date} now - The current date/time
   * @returns {{ matches: boolean, duration: number }} - Whether threshold is met and the duration
   */
  evaluateTimeThreshold(referenceDate, threshold, now) {
    if (!threshold || !threshold.enabled || !referenceDate) {
      return { matches: true, duration: 0 };
    }

    let duration = 0;

    switch (threshold.unit) {
      case DGF_TIME_UNITS.BUSINESS_DAYS:
        duration = getBusinessDaysBetween(referenceDate, now);
        break;
      case DGF_TIME_UNITS.CALENDAR_DAYS:
        duration = getCalendarDaysBetween(referenceDate, now);
        break;
      case DGF_TIME_UNITS.HOURS:
        duration = (now - referenceDate) / (1000 * 60 * 60);
        break;
      default:
        this.logger.warn('[DGFReportService] Unknown time unit', { unit: threshold.unit });
        return { matches: true, duration: 0 };
    }

    const matches = this.evaluateOperator(duration, threshold.operator, threshold.value);
    return { matches, duration };
  }

  /**
   * Fetch projects in "In Review" status with duration info
   * Uses configurable rule for status value and time threshold
   * @returns {Promise<Array>} Projects with inReviewSince and daysInReview
   */
  async getProjectsInReview() {
    try {
      this.logger.info('[DGFReportService] Fetching projects in review');

      // Get the rule configuration
      const rule = await this.getRuleByCategory(DGF_CATEGORIES.PROJECTS_IN_REVIEW);
      if (!rule) {
        this.logger.info('[DGFReportService] Projects In Review rule is disabled');
        return [];
      }

      const condition = rule.condition;
      const statusValue = condition?.value || PROJECT_STATUS.IN_REVIEW;
      const timeThreshold = condition?.timeThreshold;
      const additionalFilters = condition?.additionalFilters || [];

      const projectsRef = collection(db, 'projects');
      const q = query(projectsRef, where('status', '==', statusValue));
      const snapshot = await getDocs(q);

      const projects = [];
      const now = new Date();

      for (const docSnap of snapshot.docs) {
        const project = { id: docSnap.id, ...docSnap.data() };

        // Apply additional filters from rule configuration
        if (!this.matchesAdditionalFilters(project, additionalFilters)) {
          continue;
        }

        // Get status change timestamp from activity logs
        let statusChangeDate = await this.getFieldChangeDate(
          'projects',
          docSnap.id,
          'status',
          statusValue
        );

        // Fall back to updatedAt or createdAt if no activity log
        if (!statusChangeDate) {
          statusChangeDate = toDate(project.updatedAt) || toDate(project.createdAt) || null;
        }

        project.inReviewSince = statusChangeDate;
        project.daysInReview = statusChangeDate
          ? getBusinessDaysBetween(statusChangeDate, now)
          : null;

        // Apply time threshold filter if configured
        if (timeThreshold?.enabled && statusChangeDate) {
          const { matches } = this.evaluateTimeThreshold(statusChangeDate, timeThreshold, now);
          if (!matches) {
            continue; // Skip projects that don't meet the threshold
          }
        }

        // Attach rule to project for UI to use
        project.rule = rule;
        projects.push(project);
      }

      // Sort by longest duration first
      projects.sort((a, b) => (b.daysInReview || 0) - (a.daysInReview || 0));

      this.logger.info('[DGFReportService] Found projects in review', {
        count: projects.length
      });

      return projects;
    } catch (error) {
      // A stale-counts refresh can still be in flight when sign-out revokes the
      // token; permission-denied here is that teardown race, not a rules bug.
      this.logger.firestoreListenerError('[DGFReportService] Failed to get projects in review', error);
      throw error;
    }
  }

  /**
   * Fetch deliverables meeting DGF criteria
   * @returns {Promise<Object>} Categorized deliverables
   */
  async getDGFDeliverables() {
    try {
      this.logger.info('[DGFReportService] Fetching DGF deliverables');

      // Fetch enabled rules first
      const rules = await this.getEnabledRules();

      // Fetch all projects first to build a name lookup map
      const projectsRef = collection(db, 'projects');
      const projectsSnapshot = await getDocs(projectsRef);
      const projectNameMap = {};
      for (const docSnap of projectsSnapshot.docs) {
        const project = docSnap.data();
        projectNameMap[docSnap.id] = project.name || docSnap.id;
      }

      const deliverablesRef = collection(db, 'deliverables');
      const snapshot = await getDocs(deliverablesRef);

      this.logger.info('[DGFReportService] Total deliverables fetched', {
        count: snapshot.docs.length
      });

      const results = {
        workingOnItOverdue: [],
        internalReviewOverdue: [],
        clientReviewOverdue: [],
        stuckTooLong: [],
        dueSoon: [],
        pastDue: [],
        debug: [] // Track all deliverables with why they don't qualify
      };

      const now = new Date();

      // Count by status/stage for debugging
      const statusCounts = {};
      const stageCounts = {};

      for (const docSnap of snapshot.docs) {
        const deliverable = { id: docSnap.id, ...docSnap.data() };

        // Add project name from lookup map
        if (deliverable.projectId && projectNameMap[deliverable.projectId]) {
          deliverable.projectName = projectNameMap[deliverable.projectId];
        }

        // Track counts for debugging
        statusCounts[deliverable.status || 'no_status'] = (statusCounts[deliverable.status || 'no_status'] || 0) + 1;
        stageCounts[deliverable.stage || 'no_stage'] = (stageCounts[deliverable.stage || 'no_stage'] || 0) + 1;

        await this.categorizeDeliverableByRules(deliverable, rules, results, now);
      }

      this.logger.info('[DGFReportService] Deliverable status breakdown', statusCounts);
      this.logger.info('[DGFReportService] Deliverable stage breakdown', stageCounts);

      // Sort each category by duration (longest first)
      results.workingOnItOverdue.sort((a, b) => (b.businessDaysInStatus || 0) - (a.businessDaysInStatus || 0));
      results.internalReviewOverdue.sort((a, b) => (b.businessDaysInStage || 0) - (a.businessDaysInStage || 0));
      results.clientReviewOverdue.sort((a, b) => (b.businessDaysInStage || 0) - (a.businessDaysInStage || 0));
      results.stuckTooLong.sort((a, b) => (b.daysStuck || 0) - (a.daysStuck || 0));
      results.dueSoon.sort((a, b) => (a.hoursUntilDue || 0) - (b.hoursUntilDue || 0));
      results.pastDue.sort((a, b) => (b.daysOverdue || 0) - (a.daysOverdue || 0));

      this.logger.info('[DGFReportService] DGF deliverables categorized', {
        workingOnItOverdue: results.workingOnItOverdue.length,
        internalReviewOverdue: results.internalReviewOverdue.length,
        clientReviewOverdue: results.clientReviewOverdue.length,
        stuckTooLong: results.stuckTooLong.length,
        dueSoon: results.dueSoon.length,
        pastDue: results.pastDue.length
      });

      return results;
    } catch (error) {
      this.logger.firestoreListenerError('[DGFReportService] Failed to get DGF deliverables', error);
      throw error;
    }
  }

  /**
   * Get fallback date from deliverable's updatedAt or createdAt
   * @param {Object} deliverable
   * @returns {Date|null}
   */
  getFallbackDate(deliverable) {
    return toDate(deliverable.updatedAt) || toDate(deliverable.createdAt) || null;
  }

  /**
   * Categorize a deliverable into DGF buckets based on configured rules
   * @param {Object} deliverable - The deliverable to categorize
   * @param {Array} rules - Enabled deliverable rules from configuration
   * @param {Object} results - Results object to populate
   * @param {Date} now - Current date/time
   */
  async categorizeDeliverableByRules(deliverable, rules, results, now) {
    const debugInfo = {
      id: deliverable.id,
      title: deliverable.title,
      projectName: deliverable.projectName,
      status: deliverable.status,
      stage: deliverable.stage,
      dueDate: deliverable.dueDate ? toDate(deliverable.dueDate)?.toISOString() : null,
      matchedCategories: [],
      rejectionReasons: []
    };

    // Skip completed deliverables
    if (deliverable.status === 'done' || deliverable.stage === 'complete') {
      debugInfo.rejectionReasons.push('Completed (status=done or stage=complete)');
      results.debug.push(debugInfo);
      return;
    }

    // Filter to deliverable rules only
    const deliverableRules = rules.filter(
      r => r.condition?.entityType === DGF_ENTITY_TYPES.DELIVERABLE
    );

    for (const rule of deliverableRules) {
      const condition = rule.condition;
      const category = rule.category;

      // Handle special due date rules (dueSoon, pastDue)
      if (condition.field === 'dueDate') {
        await this.evaluateDueDateRule(deliverable, rule, results, now, debugInfo);
        continue;
      }

      // Check primary field match
      const actualValue = deliverable[condition.field];
      if (!this.evaluateOperator(actualValue, condition.operator, condition.value)) {
        debugInfo.rejectionReasons.push(
          `${rule.name}: ${condition.field} is "${actualValue}" (need "${condition.value}")`
        );
        continue;
      }

      // Check additional filters
      if (!this.matchesAdditionalFilters(deliverable, condition.additionalFilters)) {
        debugInfo.rejectionReasons.push(`${rule.name}: Failed additional filters`);
        continue;
      }

      // Get reference date for time-based check
      let referenceDate = null;
      if (condition.timeThreshold?.enabled) {
        // Try to get the field change date from activity logs
        referenceDate = await this.getFieldChangeDate(
          'deliverables',
          deliverable.id,
          condition.field,
          condition.value
        );
        // Fall back to updatedAt/createdAt if no activity log
        if (!referenceDate) {
          referenceDate = this.getFallbackDate(deliverable);
        }

        if (!referenceDate) {
          debugInfo.rejectionReasons.push(`${rule.name}: No date found for ${condition.field} change`);
          continue;
        }

        // Evaluate time threshold using configured values
        const { matches, duration } = this.evaluateTimeThreshold(
          referenceDate,
          condition.timeThreshold,
          now
        );

        if (!matches) {
          const unit = condition.timeThreshold.unit === DGF_TIME_UNITS.BUSINESS_DAYS
            ? 'business day(s)'
            : condition.timeThreshold.unit === DGF_TIME_UNITS.CALENDAR_DAYS
              ? 'calendar day(s)'
              : 'hours';
          debugInfo.rejectionReasons.push(
            `${rule.name}: Duration is ${Math.round(duration)} ${unit} ` +
            `(need ${condition.timeThreshold.operator} ${condition.timeThreshold.value})`
          );
          continue;
        }

        // Set duration fields based on rule's display config
        if (condition.field === 'status') {
          deliverable.statusChangedAt = referenceDate;
          // Set the appropriate duration field
          if (rule.display?.durationField) {
            deliverable[rule.display.durationField] = Math.round(duration);
          } else {
            deliverable.businessDaysInStatus = Math.round(duration);
          }
        } else if (condition.field === 'stage') {
          deliverable.stageChangedAt = referenceDate;
          if (rule.display?.durationField) {
            deliverable[rule.display.durationField] = Math.round(duration);
          } else {
            deliverable.businessDaysInStage = Math.round(duration);
          }
        }
      }

      // Rule matched! Add to results
      if (results[category]) {
        results[category].push({ ...deliverable, rule });
        debugInfo.matchedCategories.push(category);
      } else {
        this.logger.warn('[DGFReportService] Unknown category in rule', { category, ruleName: rule.name });
      }
    }

    results.debug.push(debugInfo);
  }

  /**
   * Evaluate due date rules (dueSoon, pastDue) for a deliverable
   * @param {Object} deliverable
   * @param {Object} rule
   * @param {Object} results
   * @param {Date} now
   * @param {Object} debugInfo
   */
  async evaluateDueDateRule(deliverable, rule, results, now, debugInfo) {
    const condition = rule.condition;
    const category = rule.category;

    if (!deliverable.dueDate) {
      debugInfo.rejectionReasons.push(`${rule.name}: No dueDate set`);
      return;
    }

    const dueDate = toDate(deliverable.dueDate);
    if (!dueDate) {
      debugInfo.rejectionReasons.push(`${rule.name}: Invalid dueDate`);
      return;
    }

    // Check additional filters (e.g., exclude certain stages)
    if (!this.matchesAdditionalFilters(deliverable, condition.additionalFilters)) {
      debugInfo.rejectionReasons.push(`${rule.name}: Failed additional filters`);
      return;
    }

    // Use end-of-day for due date comparison since due dates are date-only
    // (stored as midnight). "Due today" means the whole business day, not midnight.
    const dueDateEndOfDay = new Date(dueDate);
    dueDateEndOfDay.setHours(23, 59, 59, 999);
    const hoursUntilDue = (dueDateEndOfDay - now) / (1000 * 60 * 60);

    // Handle "Due Soon" rule - due within threshold hours, but not yet past due
    if (category === DGF_CATEGORIES.DUE_SOON) {
      const thresholdHours = condition.timeThreshold?.value || 24;

      if (hoursUntilDue > 0 && hoursUntilDue <= thresholdHours) {
        deliverable.hoursUntilDue = Math.round(hoursUntilDue);
        results.dueSoon.push({ ...deliverable, rule });
        debugInfo.matchedCategories.push('dueSoon');
      } else if (hoursUntilDue > thresholdHours) {
        const daysUntil = Math.floor(hoursUntilDue / 24);
        debugInfo.rejectionReasons.push(
          `${rule.name}: ${daysUntil} day(s) until due (need ≤${thresholdHours} hours)`
        );
      }
      // Note: if hoursUntilDue <= 0, it's past due, handled by pastDue rule
    }

    // Handle "Past Due" rule - due date has passed (or is due right now)
    if (category === DGF_CATEGORIES.PAST_DUE) {
      if (hoursUntilDue <= 0) {
        const daysOverdue = getCalendarDaysBetween(dueDate, now);
        deliverable.daysOverdue = daysOverdue;
        results.pastDue.push({ ...deliverable, rule });
        debugInfo.matchedCategories.push('pastDue');
      } else {
        debugInfo.rejectionReasons.push(`${rule.name}: Not yet past due`);
      }
    }
  }

  /**
   * Get upcoming events with no assigned personnel
   * Uses configurable rule for type exclusions
   * @returns {Promise<Array>} Events missing personnel
   */
  async getEventsMissingPersonnel() {
    try {
      this.logger.info('[DGFReportService] Fetching events missing personnel');

      // Get the rule configuration
      const rule = await this.getRuleByCategory(DGF_CATEGORIES.EVENTS_MISSING_PERSONNEL);
      if (!rule) {
        this.logger.info('[DGFReportService] Events Missing Personnel rule is disabled');
        return [];
      }

      const condition = rule.condition;
      const additionalFilters = condition?.additionalFilters || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const eventsRef = collection(db, 'calendar');
      const q = query(
        eventsRef,
        where('date', '>=', Timestamp.fromDate(today)),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      const events = [];

      for (const docSnap of snapshot.docs) {
        const event = { id: docSnap.id, ...docSnap.data() };

        // Apply additional filters from rule configuration (e.g., type exclusions)
        if (!this.matchesAdditionalFilters(event, additionalFilters)) {
          continue;
        }

        // No staff and no freelancers (externalAssignees) = missing personnel
        const hasNoAssignees = !event.assignedUserIds?.length && !event.externalAssignees?.length;
        if (!hasNoAssignees) continue;

        const eventDate = toDate(event.date);
        if (!eventDate) continue;

        event.daysUntilEvent = getCalendarDaysBetween(today, eventDate);
        event.rule = rule;
        events.push(event);
      }

      this.logger.info('[DGFReportService] Found events missing personnel', {
        count: events.length
      });

      return events;
    } catch (error) {
      this.logger.error('[DGFReportService] Failed to get events missing personnel', error);
      throw error;
    }
  }

  /**
   * Get events needing post-event reconciliation
   * Uses configurable rule for time threshold and type exclusions
   * @returns {Promise<Array>} Events needing reconciliation
   */
  async getEventsNeedingReconciliation() {
    try {
      this.logger.info('[DGFReportService] Fetching events needing reconciliation');

      // Get the rule configuration
      const rule = await this.getRuleByCategory(DGF_CATEGORIES.EVENTS_NEEDING_RECONCILIATION);
      if (!rule) {
        this.logger.info('[DGFReportService] Events Needing Reconciliation rule is disabled');
        return [];
      }

      const condition = rule.condition;
      const timeThreshold = condition?.timeThreshold;
      const additionalFilters = condition?.additionalFilters || [];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const eventsRef = collection(db, 'calendar');
      const q = query(
        eventsRef,
        where('date', '<', Timestamp.fromDate(today))
      );

      const snapshot = await getDocs(q);
      const events = [];

      for (const docSnap of snapshot.docs) {
        const event = { id: docSnap.id, ...docSnap.data() };

        // Apply additional filters from rule configuration
        // This includes postEventReconciliation != true and type exclusions
        if (!this.matchesAdditionalFilters(event, additionalFilters)) {
          continue;
        }

        const eventDate = toDate(event.date);
        if (!eventDate) continue;

        // Apply time threshold from rule configuration
        if (timeThreshold?.enabled) {
          const { matches, duration } = this.evaluateTimeThreshold(eventDate, timeThreshold, today);
          if (!matches) {
            continue;
          }
          event.daysSinceEvent = Math.round(duration);
        } else {
          // Fallback to calendar days if no threshold configured
          event.daysSinceEvent = getCalendarDaysBetween(eventDate, today);
        }

        event.rule = rule;
        events.push(event);
      }

      // Sort by most days since event first (longest waiting)
      events.sort((a, b) => (b.daysSinceEvent || 0) - (a.daysSinceEvent || 0));

      this.logger.info('[DGFReportService] Found events needing reconciliation', {
        count: events.length
      });

      return events;
    } catch (error) {
      this.logger.error('[DGFReportService] Failed to get events needing reconciliation', error);
      throw error;
    }
  }

  /**
   * Get when a field changed to a specific value from activity logs
   * @param {string} collectionName - 'projects' or 'deliverables'
   * @param {string} docId - Document ID
   * @param {string} fieldName - Field name to look for
   * @param {string} fieldValue - Value the field changed to
   * @returns {Promise<Date|null>}
   */
  async getFieldChangeDate(collectionName, docId, fieldName, fieldValue) {
    try {
      const activityRef = collection(db, collectionName, docId, 'activityLogs');
      // Use simpler query to avoid composite index requirement
      // Filter for field first, then filter client-side for newValue
      const q = query(
        activityRef,
        where('field', '==', fieldName),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);

      // Find the most recent entry where newValue matches
      for (const doc of snapshot.docs) {
        const data = doc.data();
        if (data.newValue === fieldValue) {
          return toDate(data.timestamp);
        }
      }

      return null;
    } catch {
      // Activity log query might fail if index doesn't exist or no logs
      // This is expected for items without activity history - don't log as warning
      return null;
    }
  }

  /**
   * Get total counts for tab badges (fetches fresh data and saves to cache)
   * Respects each rule's includeInBadgeCount setting
   * @returns {Promise<Object>} Counts for each tab
   */
  async getCounts(forceRefresh = false) {
    try {
      // Clear rules cache if force refreshing to pick up any rule changes
      if (forceRefresh) {
        this.clearRulesCache();
      }

      const [projects, deliverables, events, missingPersons, rules] = await Promise.all([
        this.getProjectsInReview(),
        this.getDGFDeliverables(),
        this.getEventsNeedingReconciliation(),
        this.getEventsMissingPersonnel(),
        this.getEnabledRules()
      ]);

      // Build a map of category -> includeInBadgeCount
      const badgeCountMap = {};
      rules.forEach(rule => {
        badgeCountMap[rule.category] = rule.includeInBadgeCount !== false;
      });

      // Helper to check if category should be included
      const shouldCount = (category) => badgeCountMap[category] !== false;

      // Calculate deliverable count based on rules
      let deliverableCount = 0;
      if (shouldCount(DGF_CATEGORIES.WORKING_ON_IT_OVERDUE)) {
        deliverableCount += deliverables.workingOnItOverdue.length;
      }
      if (shouldCount(DGF_CATEGORIES.INTERNAL_REVIEW_OVERDUE)) {
        deliverableCount += deliverables.internalReviewOverdue.length;
      }
      // clientReviewOverdue is excluded by default (includeInBadgeCount: false in defaults)
      if (shouldCount(DGF_CATEGORIES.CLIENT_REVIEW_OVERDUE)) {
        deliverableCount += deliverables.clientReviewOverdue.length;
      }
      if (shouldCount(DGF_CATEGORIES.STUCK_TOO_LONG)) {
        deliverableCount += deliverables.stuckTooLong.length;
      }
      if (shouldCount(DGF_CATEGORIES.DUE_SOON)) {
        deliverableCount += deliverables.dueSoon.length;
      }
      if (shouldCount(DGF_CATEGORIES.PAST_DUE)) {
        deliverableCount += deliverables.pastDue.length;
      }

      // Calculate other counts based on rules
      const projectCount = shouldCount(DGF_CATEGORIES.PROJECTS_IN_REVIEW) ? projects.length : 0;
      const eventCount = shouldCount(DGF_CATEGORIES.EVENTS_NEEDING_RECONCILIATION) ? events.length : 0;
      const missingPersonsCount = shouldCount(DGF_CATEGORIES.EVENTS_MISSING_PERSONNEL) ? missingPersons.length : 0;

      const counts = {
        projects: projectCount,
        deliverables: deliverableCount,
        events: eventCount,
        missingPersons: missingPersonsCount,
        total: projectCount + deliverableCount + eventCount + missingPersonsCount,
        // Also include raw counts for display purposes
        rawCounts: {
          projects: projects.length,
          deliverables: {
            workingOnItOverdue: deliverables.workingOnItOverdue.length,
            internalReviewOverdue: deliverables.internalReviewOverdue.length,
            clientReviewOverdue: deliverables.clientReviewOverdue.length,
            stuckTooLong: deliverables.stuckTooLong.length,
            dueSoon: deliverables.dueSoon.length,
            pastDue: deliverables.pastDue.length
          },
          events: events.length,
          missingPersons: missingPersons.length
        }
      };

      // Save counts to Firestore cache
      await this.saveCachedCounts(counts);

      return {
        ...counts,
        projectsData: projects,
        deliverablesData: deliverables,
        eventsData: events,
        missingPersonsData: missingPersons,
        rules // Include rules for the view to use
      };
    } catch (error) {
      this.logger.firestoreListenerError('[DGFReportService] Failed to get counts', error);
      throw error;
    }
  }

  /**
   * Save DGF report counts to Firestore for navbar display
   * @param {Object} counts - The counts object
   */
  async saveCachedCounts(counts) {
    try {
      const reportRef = doc(db, 'reports', 'DGFReport');
      await setDoc(reportRef, {
        counts: {
          projects: counts.projects,
          deliverables: counts.deliverables,
          events: counts.events,
          missingPersons: counts.missingPersons,
          total: counts.total
        },
        lastUpdated: serverTimestamp()
      }, { merge: true });

      this.logger.info('[DGFReportService] Saved counts to cache', counts);
    } catch (error) {
      this.logger.warn('[DGFReportService] Failed to save counts to cache', error);
      // Don't throw - caching failure shouldn't break the report
    }
  }

  /**
   * Get cached DGF report counts from Firestore
   * @returns {Promise<Object|null>} Cached counts or null if not available
   */
  async getCachedCounts() {
    try {
      const reportRef = doc(db, 'reports', 'DGFReport');
      const docSnap = await getDoc(reportRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        this.logger.info('[DGFReportService] Retrieved cached counts', data.counts);
        return {
          counts: data.counts || { projects: 0, deliverables: 0, events: 0, missingPersons: 0, total: 0 },
          lastUpdated: toDate(data.lastUpdated)
        };
      }

      return null;
    } catch (error) {
      this.logger.warn('[DGFReportService] Failed to get cached counts', error);
      return null;
    }
  }

  /**
   * Subscribe to real-time updates of cached DGF report counts
   * @param {Function} callback - Function called with counts updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToCachedCounts(callback) {
    const reportRef = doc(db, 'reports', 'DGFReport');

    return onSnapshot(reportRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        callback({
          counts: data.counts || { projects: 0, deliverables: 0, events: 0, missingPersons: 0, total: 0 },
          lastUpdated: toDate(data.lastUpdated)
        });
      } else {
        callback({
          counts: { projects: 0, deliverables: 0, events: 0, missingPersons: 0, total: 0 },
          lastUpdated: null
        });
      }
    }, (error) => {
      this.logger.warn('[DGFReportService] Failed to subscribe to cached counts', error);
      callback({
        counts: { projects: 0, deliverables: 0, events: 0, missingPersons: 0, total: 0 },
        lastUpdated: null
      });
    });
  }
}

export const dgfReportService = new DGFReportService();
